/**
 * Уведомления сотрудника (R-117): колокольчик `GET /admin/notifications`,
 * проверка `POST /admin/notifications/staff-test` и адрес живых событий
 * `/admin/ws`. Здесь — только чистые правила: что показать и куда вести.
 */
import type { components, paths } from '@dorixona/api'
import { parseMs, withCountRu } from '@dorixona/utils'
import type { IconName } from '../../ui/Icon'

export type StaffNotificationDto = components['schemas']['StaffNotification']

/** Ответ проверки `POST /admin/notifications/staff-test`. */
export type StaffTestDto =
  paths['/api/v1/admin/notifications/staff-test']['post']['responses'][200]['content']['application/json']

export interface StaffNotification {
  id: number
  template: string
  title: string
  body: string
  /** Заказ, о котором уведомление; null — проверочное. */
  orderId: number | null
  read: boolean
  /** Когда пришло, мс. */
  at: number
}

export function toNotification(dto: StaffNotificationDto): StaffNotification {
  return {
    id: dto.id ?? 0,
    template: dto.template ?? '',
    title: dto.title ?? '',
    body: dto.body ?? '',
    orderId: dto.order_id ?? null,
    read: Boolean(dto.read_at),
    at: parseMs(dto.created_at),
  }
}

export type Tone = 'info' | 'warning' | 'danger' | 'neutral'

export interface TemplateLook {
  icon: IconName
  tone: Tone
  /** Что это за уведомление — для читалки и подсказки у значка. */
  label: string
}

/**
 * Значок и тон по шаблону. Тоны — те же, что у статусов заказа: пришло и
 * никто не взял — info, нужен человек прямо сейчас — danger.
 */
const LOOKS: Record<string, TemplateLook> = {
  staff_order_new: { icon: 'i-box', tone: 'info', label: 'Новый заказ' },
  staff_order_rerouted: { icon: 'i-flow', tone: 'info', label: 'Заказ передан в аптеку' },
  staff_order_needs_attention: { icon: 'i-alert', tone: 'danger', label: 'Требует решения' },
  staff_test: { icon: 'i-bell', tone: 'neutral', label: 'Проверка уведомлений' },
  staff_fom_sync_stale: { icon: 'i-alert', tone: 'warning', label: 'Нет синхронизации с FOM' },
}

const UNKNOWN: TemplateLook = { icon: 'i-info', tone: 'neutral', label: 'Уведомление' }

export function templateLook(template: string): TemplateLook {
  return LOOKS[template] ?? UNKNOWN
}

/** Куда ведёт нажатие: на заказ; без заказа — никуда. */
export function notificationHref(notification: StaffNotification): string | null {
  return notification.orderId ? `/orders/${notification.orderId}` : null
}

/** Подпись к тосту о новом уведомлении: заголовок самого свежего непрочитанного. */
export function newNotificationText(items: readonly StaffNotification[]): string {
  const newest = items.find((n) => !n.read)
  return newest ? `Новое уведомление: ${newest.title}` : 'Новое уведомление'
}

export interface StaffTestResult {
  tone: 'info' | 'warning' | 'danger'
  text: string
}

/** Что сделала проверка: по строке на каждый ответ `push` (admin-push-testing.md §2). */
export function staffTestResult(dto: StaffTestDto): StaffTestResult {
  const inbox = dto.inbox === false ? 'В список не попало. ' : 'Уведомление в списке. '
  switch (dto.push) {
    case 'sent': {
      const devices = withCountRu(dto.push_devices ?? 0, ['устройство', 'устройства', 'устройств'])
      const removed = dto.push_removed
        ? ` Забыто устаревших: ${withCountRu(dto.push_removed, ['браузер', 'браузера', 'браузеров'])}.`
        : ''
      return { tone: 'info', text: `${inbox}Push отправлен: ${devices}.${removed}` }
    }
    case 'no_devices':
      return {
        tone: 'warning',
        text: `${inbox}Push не ушёл: браузер не подключён — включите уведомления в браузере.`,
      }
    case 'off':
      return { tone: 'warning', text: `${inbox}Push выключен на сервере.` }
    case 'not_connected':
      return { tone: 'warning', text: `${inbox}Push не ушёл: Firebase не подключён на сервере.` }
    case 'failed':
      return {
        tone: 'danger',
        text: `${inbox}Firebase не принял push: ${dto.push_error || 'причина не указана'}.`,
      }
    default:
      return { tone: 'warning', text: `${inbox}Сервер не сказал, что с push.` }
  }
}

/**
 * Адрес живых событий. Браузер не ставит заголовки на WebSocket, поэтому
 * токен — в `?token=` (так требует контракт). Абсолютный адрес API меняет
 * http на ws, относительный (`/api/v1`, его проксирует nginx или Vite) берёт
 * домен страницы.
 */
export function liveUrl(
  apiBaseUrl: string,
  token: string,
  page: { protocol: string; host: string },
): string {
  const base = /^https?:\/\//.test(apiBaseUrl)
    ? apiBaseUrl.replace(/^http/, 'ws')
    : `${page.protocol === 'https:' ? 'wss' : 'ws'}://${page.host}/${apiBaseUrl.replace(/^\/+/, '')}`
  return `${base.replace(/\/+$/, '')}/admin/ws?token=${encodeURIComponent(token)}`
}

/** Первая пауза перед переподключением и потолок: 1 с, 2, 4 … 30 с. */
export const LIVE_FIRST_DELAY = 1000
export const LIVE_MAX_DELAY = 30_000

export function nextLiveDelay(delay: number): number {
  return Math.min(delay * 2, LIVE_MAX_DELAY)
}
